import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import {
  archiveAssetConnection,
  archiveAssetDiagram,
  archiveDistributionBoardCircuit,
  createAssetConnection,
  createAssetDiagram,
  createBuildingLevel,
  createDistributionBoardCircuit,
  createSiteBuilding,
  createTechnicalAsset,
  createTechnicalInstallation,
  enrichProjectLocation,
  listAssetConnections,
  listAssetDiagrams,
  listAssetLocationNodes,
  listBuildingLevels,
  listConnectionsForAsset,
  listDistributionBoardCircuits,
  listSiteBuildings,
  listTechnicalAssets,
  listTechnicalInstallations,
  previewTechnicalAssetTag,
  TECHNICAL_ASSET_INSTALLATION_TYPE_OPTIONS,
  TECHNICAL_ASSET_STATUS_OPTIONS,
  TECHNICAL_ASSET_TYPE_OPTIONS,
  TECHNICAL_CONNECTION_TYPE_OPTIONS,
  TECHNICAL_DIAGRAM_TYPE_OPTIONS,
  TECHNICAL_INSTALLATION_TYPE_OPTIONS,
  SITE_BUILDING_TYPE_OPTIONS,
  BUILDING_LEVEL_CREATE_MODE_OPTIONS,
  BUILDING_LEVEL_TYPE_OPTIONS,
  DISTRIBUTION_BOARD_CIRCUIT_PHASE_OPTIONS,
  DISTRIBUTION_BOARD_CIRCUIT_STATUS_OPTIONS,
  updateAssetDiagram,
  updateSiteBuilding,
} from "../../services/technicalAssetApi";
import {
  getBagAddressDetail,
  searchBagAddresses,
} from "../../services/fireSafetyLogbookApi";
import {
  Button,
  EmptyState,
  FormField,
  PageHeader,
  SectionPanel,
  Select,
  StatusBadge,
  TextInput,
  Textarea,
} from "../../components/ui";
import {
  isDistributionBoardAssetType,
  normalizeGeoGeometry,
} from "../../utils/technicalAssetSpatial";
import { handleTechnicalAssetApiError } from "../../utils/technicalAssetApiErrors";

const ROOT_TENANT_ID = "000000000000000000000000";
const PLATFORM_TENANT_STORAGE_KEY = "technical-assets-platform-tenant-id";

const EMPTY_ASSET_FORM = {
  name: "",
  description: "",
  assetType: "generic",
  installationType: "bmi",
  status: "active",
  technicalSpecs: {
    phases: "",
    nominalVoltage: "",
    mainFuseAmps: "",
    ratedCurrentAmps: "",
    totalModules: "",
    ipRating: "",
  },
};

const EMPTY_BUILDING_FORM = {
  name: "",
  type: "other",
  code: "",
};

const EMPTY_LEVEL_FORM = {
  name: "",
  code: "",
  type: "floor",
  levelNumber: "",
  sortOrder: "",
  createMode: "empty",
};

const EMPTY_INSTALLATION_FORM = {
  name: "",
  code: "",
  installationType: "electrical",
  description: "",
};

const EMPTY_CONNECTION_FORM = {
  sourceAssetId: "",
  targetAssetId: "",
  connectionType: "dependency",
  label: "",
};

const EMPTY_CIRCUIT_FORM = {
  groupNumber: "",
  positionFrom: "",
  positionTo: "",
  modulesUsed: "1",
  name: "",
  description: "",
  status: "in_use",
  phase: "unknown",
  breakerType: "",
  breakerAmps: "",
  breakerCharacteristic: "",
  residualCurrentProtection: false,
  residualCurrentMilliAmps: "",
};

export default function TechnicalAssetsPage({ lockedClientId = "", lockedClientName = "" }) {
  return (
    <ReactFlowProvider>
      <TechnicalAssetsWorkspace
        lockedClientId={String(lockedClientId || "")}
        lockedClientName={lockedClientName}
      />
    </ReactFlowProvider>
  );
}

function TechnicalAssetsWorkspace({ lockedClientId, lockedClientName }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [assetSaving, setAssetSaving] = useState(false);
  const [diagramSaving, setDiagramSaving] = useState(false);
  const [diagramArchiving, setDiagramArchiving] = useState(false);
  const [connectionSaving, setConnectionSaving] = useState(false);
  const [buildingSaving, setBuildingSaving] = useState(false);
  const [levelSaving, setLevelSaving] = useState(false);
  const [installationSaving, setInstallationSaving] = useState(false);
  const [bagSearching, setBagSearching] = useState(false);
  const [enrichingLocation, setEnrichingLocation] = useState(false);
  const [circuitSaving, setCircuitSaving] = useState(false);
  const [archivingConnectionId, setArchivingConnectionId] = useState("");
  const [archivingCircuitId, setArchivingCircuitId] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [platformTenants, setPlatformTenants] = useState([]);
  const [locationNodes, setLocationNodes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [buildingLevels, setBuildingLevels] = useState([]);
  const [technicalInstallations, setTechnicalInstallations] = useState([]);
  const [boardCircuits, setBoardCircuits] = useState([]);
  const [boardSummary, setBoardSummary] = useState(null);
  const [bagResults, setBagResults] = useState([]);
  const [selectedBagAddress, setSelectedBagAddress] = useState(null);

  const [selectedClientId, setSelectedClientId] = useState(() => lockedClientId || "");
  const [selectedPlatformTenantId, setSelectedPlatformTenantId] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const [selectedLocationNodeId, setSelectedLocationNodeId] = useState("");
  const [selectedTechnicalInstallationId, setSelectedTechnicalInstallationId] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedDiagramId, setSelectedDiagramId] = useState("");
  const [diagramName, setDiagramName] = useState("");
  const [selectedDiagramType, setSelectedDiagramType] = useState("site");
  const [selectedAssetConnections, setSelectedAssetConnections] = useState([]);
  const [search, setSearch] = useState("");
  const [assetArchiveFilter, setAssetArchiveFilter] = useState("active");
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [bagQuery, setBagQuery] = useState("");
  const [tagPreview, setTagPreview] = useState("");
  const [assetForm, setAssetForm] = useState(EMPTY_ASSET_FORM);
  const [buildingForm, setBuildingForm] = useState(EMPTY_BUILDING_FORM);
  const [levelForm, setLevelForm] = useState(EMPTY_LEVEL_FORM);
  const [installationForm, setInstallationForm] = useState(EMPTY_INSTALLATION_FORM);
  const [connectionForm, setConnectionForm] = useState(EMPTY_CONNECTION_FORM);
  const [circuitForm, setCircuitForm] = useState(EMPTY_CIRCUIT_FORM);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowRef = useRef(null);
  const deferredSearch = useDeferredValue(search);
  const isRootMode = isPlatformRootUser(user) && !lockedClientId;

  const platformRequestOptions = useMemo(() => {
    if (!isRootMode || !selectedPlatformTenantId) return {};
    return { platformTenantId: selectedPlatformTenantId };
  }, [isRootMode, selectedPlatformTenantId]);

  const canConfigureAssets = useMemo(
    () => userHasAnyPermission(user, ["assets.configure"]),
    [user]
  );
  const canCreateAssets = useMemo(
    () => userHasAnyPermission(user, ["assets.create"]),
    [user]
  );
  const canManageConnections = useMemo(
    () => userHasAnyPermission(user, ["assets.connections.manage"]),
    [user]
  );
  const canEditDiagrams = useMemo(
    () => userHasAnyPermission(user, ["assets.diagram.edit"]),
    [user]
  );
  const canEditBoardCircuits = useMemo(
    () => userHasAnyPermission(user, ["assets.edit"]),
    [user]
  );
  const canViewConnections = useMemo(
    () => userHasAnyPermission(user, ["assets.connections.view"]),
    [user]
  );
  const canViewDiagrams = useMemo(
    () => userHasAnyPermission(user, ["assets.diagram.view"]),
    [user]
  );

  const handlePageError = useCallback(
    (err, fallbackMessage) => {
      const normalized = handleTechnicalAssetApiError(err, {
        user,
        fallbackMessage,
      });
      setError(normalized.message);
      return normalized;
    },
    [user]
  );

  const locationOptions = useMemo(() => {
    return projects.filter((project) => asId(project?.clientId) === selectedClientId);
  }, [projects, selectedClientId]);

  const scopedLocationNodes = useMemo(() => {
    return locationNodes.filter((node) => {
      const linkedProjectId = asId(node?.linkedProjectId);
      if (!selectedLocationId) return true;
      return !linkedProjectId || linkedProjectId === selectedLocationId;
    });
  }, [locationNodes, selectedLocationId]);

  const selectedClient = useMemo(
    () => clients.find((client) => asId(client?._id) === selectedClientId) || null,
    [clients, selectedClientId]
  );
  const selectedLocation = useMemo(
    () => locationOptions.find((project) => asId(project?._id) === selectedLocationId) || null,
    [locationOptions, selectedLocationId]
  );
  const selectedBuilding = useMemo(
    () => buildings.find((building) => asId(building?._id) === selectedBuildingId) || null,
    [buildings, selectedBuildingId]
  );
  const levelsForBuilding = useMemo(() => {
    return buildingLevels
      .filter((level) => asId(level?.buildingId) === selectedBuildingId)
      .sort((left, right) => Number(left?.sortOrder || 0) - Number(right?.sortOrder || 0));
  }, [buildingLevels, selectedBuildingId]);
  const selectedLevel = useMemo(
    () => levelsForBuilding.find((level) => asId(level?._id) === selectedLevelId) || null,
    [levelsForBuilding, selectedLevelId]
  );
  const technicalInstallationsInScope = useMemo(() => {
    return technicalInstallations.filter((installation) => {
      if (selectedBuildingId && asId(installation?.buildingId) !== selectedBuildingId) {
        return false;
      }
      if (selectedLevelId && asId(installation?.buildingLevelId) !== selectedLevelId) {
        return false;
      }
      return true;
    });
  }, [technicalInstallations, selectedBuildingId, selectedLevelId]);
  const selectedTechnicalInstallation = useMemo(
    () =>
      technicalInstallations.find(
        (installation) => asId(installation?._id) === selectedTechnicalInstallationId
      ) || null,
    [technicalInstallations, selectedTechnicalInstallationId]
  );
  const selectedDiagram = useMemo(
    () => diagrams.find((diagram) => asId(diagram?._id) === selectedDiagramId) || null,
    [diagrams, selectedDiagramId]
  );
  const visibleAssets = useMemo(() => {
    return filterAssetsForDiagram(assets, {
      diagramType: selectedDiagramType,
      selectedLocationId,
      selectedBuildingId,
      selectedLevelId,
      selectedTechnicalInstallationId,
      selectedLocationNodeId,
    });
  }, [
    assets,
    selectedBuildingId,
    selectedDiagramType,
    selectedLevelId,
    selectedLocationId,
    selectedLocationNodeId,
    selectedTechnicalInstallationId,
  ]);
  const selectedAsset = useMemo(() => {
    return assets.find((item) => asId(item?._id) === selectedAssetId) || null;
  }, [assets, selectedAssetId]);
  const isSelectedBoard = isDistributionBoardAssetType(selectedAsset?.assetType);
  const selectedPlatformTenant = useMemo(
    () =>
      platformTenants.find((tenant) => asId(tenant?.tenantId) === selectedPlatformTenantId) || null,
    [platformTenants, selectedPlatformTenantId]
  );

  const diagramScopeDescriptor = useMemo(() => {
    if (selectedDiagramType === "floor" && selectedLevel) {
      return `${selectedBuilding?.name || "Gebouw"} · ${selectedLevel.name}`;
    }
    if (selectedDiagramType === "vertical" && selectedBuilding) {
      return `${selectedBuilding.name} · verticaal`;
    }
    if (selectedDiagramType === "site" && selectedLocation) {
      return `${selectedLocation?.naam || selectedLocation?.title || "Locatie"} · terrein`;
    }
    return "Scope";
  }, [selectedBuilding, selectedDiagramType, selectedLevel, selectedLocation]);

  const loadBootstrapData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      let tenantHeaderConfig = {};

      if (isRootMode) {
        const tenantRes = await api.get("/admin/tenants");
        const nextPlatformTenants = Array.isArray(tenantRes?.data?.tenants)
          ? tenantRes.data.tenants
          : [];
        setPlatformTenants(nextPlatformTenants);

        const preferredTenantId = resolvePreferredPlatformTenantId(
          nextPlatformTenants,
          selectedPlatformTenantId || readStoredPlatformTenantId()
        );

        if (preferredTenantId !== selectedPlatformTenantId) {
          setSelectedPlatformTenantId(preferredTenantId);
        }

        if (!preferredTenantId) {
          clearTenantScopedState({
            setClients,
            setProjects,
            setLocationNodes,
            setAssets,
            setConnections,
            setDiagrams,
            setBuildings,
            setBuildingLevels,
            setTechnicalInstallations,
            setBoardCircuits,
            setSelectedClientId,
            setSelectedLocationId,
            setSelectedBuildingId,
            setSelectedLevelId,
            setSelectedLocationNodeId,
            setSelectedTechnicalInstallationId,
            setSelectedAssetId,
            setSelectedDiagramId,
            setSelectedAssetConnections,
            setTagPreview,
            setNodes,
            setEdges,
          });
          return;
        }

        tenantHeaderConfig = {
          headers: buildPlatformTenantHeaders(preferredTenantId),
        };
      } else {
        setPlatformTenants([]);
      }

      const [clientRes, projectRes] = await Promise.all([
        api.get("/clients", tenantHeaderConfig),
        api.get("/projects", tenantHeaderConfig),
      ]);

      const nextClients = Array.isArray(clientRes?.data) ? clientRes.data : [];
      const nextProjects = Array.isArray(projectRes?.data) ? projectRes.data : [];

      if (lockedClientId && !nextClients.some((client) => asId(client?._id) === lockedClientId)) {
        setClients([]);
        setProjects([]);
        setSelectedClientId(lockedClientId);
        setError("Deze klant bestaat niet of valt buiten je toegestane klantcontext.");
        return;
      }

      setClients(lockedClientId
        ? nextClients.filter((client) => asId(client?._id) === lockedClientId)
        : nextClients);
      setProjects(nextProjects);
      setSelectedClientId((current) => {
        if (lockedClientId) return lockedClientId;
        if (nextClients.some((client) => asId(client?._id) === current)) return current;
        return asId(nextClients[0]?._id);
      });
    } catch (err) {
      handlePageError(err, "Technische assets laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [handlePageError, isRootMode, lockedClientId, selectedPlatformTenantId, setEdges, setNodes]);

  useEffect(() => {
    if (!lockedClientId) return;
    setSelectedClientId(lockedClientId);
  }, [lockedClientId]);

  const loadScopeData = useCallback(async () => {
    if (!selectedClientId) {
      setLocationNodes([]);
      setAssets([]);
      setConnections([]);
      setDiagrams([]);
      setBuildings([]);
      setBuildingLevels([]);
      setTechnicalInstallations([]);
      return;
    }

    try {
      setScopeLoading(true);
      setError("");

      const [assetRes, connectionRes, diagramRes, locationNodeRes, buildingRes, levelRes, installationRes] =
        await Promise.all([
          listTechnicalAssets(
            {
              clientId: selectedClientId,
              locationId: selectedLocationId || undefined,
              buildingId:
                selectedDiagramType === "vertical" || selectedDiagramType === "floor"
                  ? selectedBuildingId || undefined
                  : undefined,
              search: deferredSearch || undefined,
              assetType: assetTypeFilter || undefined,
              archived: assetArchiveFilter === "archived",
              limit: 500,
            },
            platformRequestOptions
          ),
          canViewConnections
            ? listAssetConnections(
            {
              clientId: selectedClientId,
              limit: 500,
              includeArchived: false,
            },
            platformRequestOptions
            )
            : Promise.resolve({ items: [] }),
          canViewDiagrams
            ? listAssetDiagrams(
            {
              clientId: selectedClientId,
              locationId: selectedLocationId || undefined,
              buildingId:
                selectedDiagramType === "vertical" || selectedDiagramType === "floor"
                  ? selectedBuildingId || undefined
                  : undefined,
              buildingLevelId:
                selectedDiagramType === "floor" ? selectedLevelId || undefined : undefined,
              locationNodeId: selectedLocationNodeId || undefined,
              diagramType: selectedDiagramType,
              limit: 100,
            },
            platformRequestOptions
            )
            : Promise.resolve({ items: [] }),
          listAssetLocationNodes(
            {
              clientId: selectedClientId,
              limit: 200,
            },
            platformRequestOptions
          ),
          selectedLocationId
            ? listSiteBuildings(
                {
                  clientId: selectedClientId,
                  locationId: selectedLocationId,
                  limit: 200,
                },
                platformRequestOptions
              )
            : Promise.resolve({ items: [] }),
          selectedLocationId
            ? listBuildingLevels(
                {
                  clientId: selectedClientId,
                  locationId: selectedLocationId,
                  buildingId: selectedBuildingId || undefined,
                  limit: 200,
                },
                platformRequestOptions
              )
            : Promise.resolve({ items: [] }),
          selectedLocationId
            ? listTechnicalInstallations(
                {
                  clientId: selectedClientId,
                  locationId: selectedLocationId,
                  buildingId: selectedBuildingId || undefined,
                  buildingLevelId:
                    selectedDiagramType === "floor" ? selectedLevelId || undefined : undefined,
                  limit: 200,
                },
                platformRequestOptions
              )
            : Promise.resolve({ items: [] }),
        ]);

      setAssets(Array.isArray(assetRes?.items) ? assetRes.items : []);
      setConnections(Array.isArray(connectionRes?.items) ? connectionRes.items : []);
      setDiagrams(Array.isArray(diagramRes?.items) ? diagramRes.items : []);
      setLocationNodes(Array.isArray(locationNodeRes?.items) ? locationNodeRes.items : []);
      setBuildings(Array.isArray(buildingRes?.items) ? buildingRes.items : []);
      setBuildingLevels(Array.isArray(levelRes?.items) ? levelRes.items : []);
      setTechnicalInstallations(
        Array.isArray(installationRes?.items) ? installationRes.items : []
      );
    } catch (err) {
      handlePageError(err, "Scope laden mislukt");
    } finally {
      setScopeLoading(false);
    }
  }, [
    canViewConnections,
    canViewDiagrams,
    assetArchiveFilter,
    assetTypeFilter,
    deferredSearch,
    handlePageError,
    platformRequestOptions,
    selectedBuildingId,
    selectedClientId,
    selectedDiagramType,
    selectedLevelId,
    selectedLocationId,
    selectedLocationNodeId,
  ]);

  useEffect(() => {
    loadBootstrapData();
  }, [loadBootstrapData]);

  useEffect(() => {
    loadScopeData();
  }, [loadScopeData]);

  useEffect(() => {
    if (!selectedLocationId) {
      setSelectedBuildingId("");
      setSelectedLevelId("");
      setSelectedTechnicalInstallationId("");
      setSelectedDiagramType("site");
      return;
    }

    if (!buildings.length) {
      setSelectedBuildingId("");
      setSelectedLevelId("");
      if (selectedDiagramType !== "site") {
        setSelectedDiagramType("site");
      }
      return;
    }

    if (!buildings.some((building) => asId(building?._id) === selectedBuildingId)) {
      const preferredBuilding =
        buildings.find((building) => building?.isMainBuilding === true) || buildings[0] || null;
      setSelectedBuildingId(asId(preferredBuilding?._id));
    }
  }, [buildings, selectedBuildingId, selectedDiagramType, selectedLocationId]);

  useEffect(() => {
    if (selectedDiagramType !== "floor") {
      return;
    }

    if (!levelsForBuilding.length) {
      setSelectedLevelId("");
      return;
    }

    if (!levelsForBuilding.some((level) => asId(level?._id) === selectedLevelId)) {
      setSelectedLevelId(asId(levelsForBuilding[0]?._id));
    }
  }, [levelsForBuilding, selectedDiagramType, selectedLevelId]);

  useEffect(() => {
    if (!technicalInstallationsInScope.length) {
      setSelectedTechnicalInstallationId("");
      return;
    }

    if (
      !technicalInstallationsInScope.some(
        (installation) => asId(installation?._id) === selectedTechnicalInstallationId
      )
    ) {
      setSelectedTechnicalInstallationId(asId(technicalInstallationsInScope[0]?._id));
    }
  }, [selectedTechnicalInstallationId, technicalInstallationsInScope]);

  useEffect(() => {
    if (!diagrams.length) {
      setSelectedDiagramId("");
      return;
    }

    const currentExists = diagrams.some((diagram) => asId(diagram?._id) === selectedDiagramId);
    if (currentExists) return;

    const preferred = diagrams.find((diagram) => diagram?.isDefault === true) || diagrams[0];
    setSelectedDiagramId(asId(preferred?._id));
  }, [diagrams, selectedDiagramId]);

  useEffect(() => {
    setDiagramName(selectedDiagram?.name || "");
  }, [selectedDiagram]);

  useEffect(() => {
    if (!selectedClientId) {
      setTagPreview("");
      return;
    }

    let cancelled = false;
    async function loadTagPreview() {
      try {
        const preview = await previewTechnicalAssetTag(
          {
            clientId: selectedClientId,
            locationId: selectedLocationId || undefined,
            locationNodeId: selectedLocationNodeId || undefined,
            installationType: resolveAssetInstallationType(
              selectedTechnicalInstallation?.installationType,
              assetForm.installationType
            ),
            assetType: assetForm.assetType,
          },
          platformRequestOptions
        );

        if (cancelled) return;
        startTransition(() => {
          setTagPreview(String(preview?.suggestedTag || ""));
        });
      } catch {
        if (!cancelled) setTagPreview("");
      }
    }

    loadTagPreview();
    return () => {
      cancelled = true;
    };
  }, [
    assetForm.assetType,
    assetForm.installationType,
    platformRequestOptions,
    selectedClientId,
    selectedLocationId,
    selectedLocationNodeId,
    selectedTechnicalInstallation?.installationType,
  ]);

  const flowGraph = useMemo(() => {
    return buildFlowGraph({
      diagramType: selectedDiagramType,
      visibleAssets,
      allAssets: assets,
      connections,
      selectedDiagram,
      buildings,
      levels: levelsForBuilding,
      allLevels: buildingLevels,
      selectedBuilding,
      selectedLevel,
    });
  }, [
    assets,
    buildingLevels,
    buildings,
    connections,
    levelsForBuilding,
    selectedBuilding,
    selectedDiagram,
    selectedDiagramType,
    selectedLevel,
    visibleAssets,
  ]);

  useEffect(() => {
    setNodes(flowGraph.nodes);
    setEdges(flowGraph.edges);
  }, [flowGraph.edges, flowGraph.nodes, setEdges, setNodes]);

  useEffect(() => {
    if (!selectedAssetId || selectedAssetId.startsWith("transfer:") || selectedAssetId.startsWith("level:")) {
      setSelectedAssetConnections([]);
      return;
    }

    let cancelled = false;
    async function loadAssetConnections() {
      try {
        const result = await listConnectionsForAsset(
          selectedAssetId,
          { limit: 100, includeArchived: false },
          platformRequestOptions
        );
        if (!cancelled) {
          setSelectedAssetConnections(Array.isArray(result?.items) ? result.items : []);
        }
      } catch {
        if (!cancelled) setSelectedAssetConnections([]);
      }
    }

    loadAssetConnections();
    return () => {
      cancelled = true;
    };
  }, [platformRequestOptions, selectedAssetId]);

  useEffect(() => {
    if (!isSelectedBoard || !selectedAssetId) {
      setBoardCircuits([]);
      setBoardSummary(null);
      return;
    }

    let cancelled = false;
    async function loadBoardCircuits() {
      try {
        const result = await listDistributionBoardCircuits(
          { boardAssetId: selectedAssetId, limit: 250 },
          platformRequestOptions
        );
        if (cancelled) return;
        setBoardCircuits(Array.isArray(result?.items) ? result.items : []);
        setBoardSummary(result?.summary || null);
      } catch {
        if (!cancelled) {
          setBoardCircuits([]);
          setBoardSummary(null);
        }
      }
    }

    loadBoardCircuits();
    return () => {
      cancelled = true;
    };
  }, [isSelectedBoard, platformRequestOptions, selectedAssetId]);

  async function handleSearchBag() {
    if (!bagQuery.trim()) return;
    try {
      setBagSearching(true);
      const results = await searchBagAddresses(bagQuery.trim());
      setBagResults(Array.isArray(results) ? results : []);
    } catch (err) {
      handlePageError(err, "BAG/PDOK zoekactie mislukt");
    } finally {
      setBagSearching(false);
    }
  }

  async function handleSelectBagResult(result) {
    const resultId = String(result?.id || "").trim();
    if (!resultId) return;

    try {
      setBagSearching(true);
      const detail = await getBagAddressDetail(resultId);
      setSelectedBagAddress(detail ? { ...result, ...detail } : result);
    } catch (err) {
      handlePageError(err, "Adresdetail ophalen mislukt");
    } finally {
      setBagSearching(false);
    }
  }

  async function handleEnrichLocation() {
    if (!selectedLocationId || !selectedBagAddress) return;

    try {
      setEnrichingLocation(true);
      setSuccessMessage("");
      await enrichProjectLocation(
        selectedLocationId,
        {
          addressId:
            selectedBagAddress?.nummeraanduidingIdentificatie ||
            selectedBagAddress?.id,
          source: String(selectedBagAddress?.source || "").trim().toLowerCase() === "pdok" ? "pdok" : "bag",
          selectedPandIds: Array.isArray(selectedBagAddress?.pandIdentificaties)
            ? selectedBagAddress.pandIdentificaties
            : [],
        },
        platformRequestOptions
      );
      setSuccessMessage("Locatie verrijkt met BAG/PDOK en gebouwen bijgewerkt.");
      await loadBootstrapData();
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Locatieverrijking mislukt");
    } finally {
      setEnrichingLocation(false);
    }
  }

  async function handleCreateBuilding(event) {
    event.preventDefault();
    if (!selectedClientId || !selectedLocationId) return;

    try {
      setBuildingSaving(true);
      await createSiteBuilding(
        {
          clientId: selectedClientId,
          locationId: selectedLocationId,
          name: buildingForm.name.trim(),
          code: buildingForm.code.trim() || undefined,
          type: buildingForm.type,
          isMainBuilding: buildings.length === 0,
        },
        platformRequestOptions
      );
      setBuildingForm(EMPTY_BUILDING_FORM);
      setSuccessMessage("Gebouw toegevoegd.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Gebouw aanmaken mislukt");
    } finally {
      setBuildingSaving(false);
    }
  }

  async function handleUpdateBuildingSelection(buildingId, selectionStatus) {
    try {
      await updateSiteBuilding(
        buildingId,
        {
          selectionStatus,
          geometryMetadata: {
            userConfirmed: selectionStatus === "confirmed",
          },
        },
        platformRequestOptions
      );
      setSuccessMessage(
        selectionStatus === "confirmed"
          ? "BAG-pand bevestigd."
          : "BAG-pand genegeerd."
      );
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Gebouwstatus bijwerken mislukt");
    }
  }

  async function handleCreateLevel(event) {
    event.preventDefault();
    if (!selectedClientId || !selectedLocationId || !selectedBuildingId) return;

    try {
      setLevelSaving(true);
      await createBuildingLevel(
        {
          clientId: selectedClientId,
          locationId: selectedLocationId,
          buildingId: selectedBuildingId,
          name: levelForm.name.trim(),
          code: levelForm.code.trim() || undefined,
          type: levelForm.type,
          levelNumber: levelForm.levelNumber === "" ? null : Number(levelForm.levelNumber),
          sortOrder: levelForm.sortOrder === "" ? undefined : Number(levelForm.sortOrder),
          createMode: levelForm.createMode,
        },
        platformRequestOptions
      );
      setLevelForm(EMPTY_LEVEL_FORM);
      setSuccessMessage("Verdieping toegevoegd.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Verdieping aanmaken mislukt");
    } finally {
      setLevelSaving(false);
    }
  }

  async function handleCreateTechnicalInstallation(event) {
    event.preventDefault();
    if (!selectedClientId || !selectedLocationId) return;

    try {
      setInstallationSaving(true);
      const created = await createTechnicalInstallation(
        {
          clientId: selectedClientId,
          locationId: selectedLocationId,
          buildingId: selectedBuildingId || null,
          buildingLevelId:
            selectedDiagramType === "floor" ? selectedLevelId || null : null,
          locationNodeId: selectedLocationNodeId || null,
          installationType: installationForm.installationType,
          name: installationForm.name.trim(),
          code: installationForm.code.trim() || undefined,
          description: installationForm.description.trim() || undefined,
        },
        platformRequestOptions
      );
      setInstallationForm(EMPTY_INSTALLATION_FORM);
      setSelectedTechnicalInstallationId(asId(created?._id));
      setSuccessMessage("Technische installatie toegevoegd.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Technische installatie aanmaken mislukt");
    } finally {
      setInstallationSaving(false);
    }
  }

  async function handleCreateAsset(event) {
    event.preventDefault();
    if (!selectedClientId) return;

    try {
      setAssetSaving(true);
      const payload = {
        clientId: selectedClientId,
        locationId: selectedLocationId || undefined,
        buildingId: selectedBuildingId || undefined,
        buildingLevelId:
          selectedDiagramType === "floor" ? selectedLevelId || undefined : undefined,
        locationNodeId: selectedLocationNodeId || undefined,
        installationId: selectedTechnicalInstallationId || undefined,
        name: assetForm.name.trim(),
        description: assetForm.description.trim() || undefined,
        assetType: assetForm.assetType,
        installationType: resolveAssetInstallationType(
          selectedTechnicalInstallation?.installationType,
          assetForm.installationType
        ),
        status: assetForm.status,
        useSuggestedTag: true,
      };

      if (isDistributionBoardAssetType(assetForm.assetType)) {
        payload.technicalSpecs = sanitizeTechnicalSpecs(assetForm.technicalSpecs);
      }

      const created = await createTechnicalAsset(payload, platformRequestOptions);

      setAssetForm(EMPTY_ASSET_FORM);
      setSelectedAssetId(asId(created?._id));
      setConnectionForm((current) => ({
        ...current,
        sourceAssetId: current.sourceAssetId || asId(created?._id),
      }));
      setSuccessMessage("Asset aangemaakt.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Asset aanmaken mislukt");
    } finally {
      setAssetSaving(false);
    }
  }

  async function handleCreateConnection(event) {
    event.preventDefault();
    if (!selectedClientId) return;

    try {
      setConnectionSaving(true);
      await createAssetConnection(
        {
          clientId: selectedClientId,
          sourceAssetId: connectionForm.sourceAssetId,
          targetAssetId: connectionForm.targetAssetId,
          connectionType: connectionForm.connectionType,
          label: connectionForm.label.trim() || undefined,
        },
        platformRequestOptions
      );
      setConnectionForm(EMPTY_CONNECTION_FORM);
      setSuccessMessage("Verbinding opgeslagen.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Verbinding aanmaken mislukt");
    } finally {
      setConnectionSaving(false);
    }
  }

  async function handleArchiveConnection(connectionId) {
    if (!connectionId) return;
    try {
      setArchivingConnectionId(connectionId);
      await archiveAssetConnection(connectionId, platformRequestOptions);
      setSuccessMessage("Verbinding gearchiveerd.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Verbinding archiveren mislukt");
    } finally {
      setArchivingConnectionId("");
    }
  }

  async function handleCreateCircuit(event) {
    event.preventDefault();
    if (!selectedAssetId) return;

    try {
      setCircuitSaving(true);
      const result = await createDistributionBoardCircuit(
        {
          boardAssetId: selectedAssetId,
          groupNumber: circuitForm.groupNumber.trim() || undefined,
          positionFrom:
            circuitForm.positionFrom === "" ? null : Number(circuitForm.positionFrom),
          positionTo: circuitForm.positionTo === "" ? null : Number(circuitForm.positionTo),
          modulesUsed: Number(circuitForm.modulesUsed || 0),
          name: circuitForm.name.trim() || undefined,
          description: circuitForm.description.trim() || undefined,
          status: circuitForm.status,
          phase: circuitForm.phase,
          breakerType: circuitForm.breakerType.trim() || undefined,
          breakerAmps:
            circuitForm.breakerAmps === "" ? null : Number(circuitForm.breakerAmps),
          breakerCharacteristic:
            circuitForm.breakerCharacteristic.trim() || undefined,
          residualCurrentProtection: circuitForm.residualCurrentProtection === true,
          residualCurrentMilliAmps:
            circuitForm.residualCurrentMilliAmps === ""
              ? null
              : Number(circuitForm.residualCurrentMilliAmps),
        },
        platformRequestOptions
      );

      setCircuitForm(EMPTY_CIRCUIT_FORM);
      setBoardSummary(result?.summary || null);
      setSuccessMessage("Groep toegevoegd aan de verdeelkast.");
      const listResult = await listDistributionBoardCircuits(
        { boardAssetId: selectedAssetId, limit: 250 },
        platformRequestOptions
      );
      setBoardCircuits(Array.isArray(listResult?.items) ? listResult.items : []);
      setBoardSummary(listResult?.summary || result?.summary || null);
    } catch (err) {
      handlePageError(err, "Groep opslaan mislukt");
    } finally {
      setCircuitSaving(false);
    }
  }

  async function handleArchiveCircuit(circuitId) {
    if (!circuitId) return;
    try {
      setArchivingCircuitId(circuitId);
      const result = await archiveDistributionBoardCircuit(
        circuitId,
        platformRequestOptions
      );
      setBoardSummary(result?.summary || null);
      setSuccessMessage("Groep gearchiveerd.");
      const listResult = await listDistributionBoardCircuits(
        { boardAssetId: selectedAssetId, limit: 250 },
        platformRequestOptions
      );
      setBoardCircuits(Array.isArray(listResult?.items) ? listResult.items : []);
      setBoardSummary(listResult?.summary || result?.summary || null);
    } catch (err) {
      handlePageError(err, "Groep archiveren mislukt");
    } finally {
      setArchivingCircuitId("");
    }
  }

  async function handleSaveDiagram() {
    if (!selectedClientId) return;

    try {
      setDiagramSaving(true);
      const payload = {
        clientId: selectedClientId,
        locationId: selectedLocationId,
        buildingId:
          selectedDiagramType === "vertical" || selectedDiagramType === "floor"
            ? selectedBuildingId || undefined
            : undefined,
        buildingLevelId:
          selectedDiagramType === "floor" ? selectedLevelId || undefined : undefined,
        locationNodeId: selectedLocationNodeId || undefined,
        diagramType: selectedDiagramType,
        name: diagramName.trim() || buildDiagramName({
          selectedLocation,
          selectedBuilding,
          selectedLevel,
          selectedDiagramType,
        }),
        isDefault: true,
        assetPositions: nodes
          .filter((node) => !String(node?.id || "").includes(":"))
          .map((node) => ({
            assetId: node.id,
            x: roundCoordinate(node.position.x),
            y: roundCoordinate(node.position.y),
            width: node.measured?.width || node.width || null,
            height: node.measured?.height || node.height || null,
          })),
        viewport: reactFlowRef.current?.getViewport?.() || {
          x: 0,
          y: 0,
          zoom: 1,
        },
        background: flowGraph.backgroundPayload || undefined,
        filters: {
          diagramType: selectedDiagramType,
          buildingId: selectedBuildingId || "",
          buildingLevelId: selectedLevelId || "",
          installationId: selectedTechnicalInstallationId || "",
          locationNodeId: selectedLocationNodeId || "",
        },
      };

      if (selectedDiagramId) {
        await updateAssetDiagram(selectedDiagramId, payload, platformRequestOptions);
      } else {
        const created = await createAssetDiagram(payload, platformRequestOptions);
        setSelectedDiagramId(asId(created?._id));
      }

      setSuccessMessage("Diagramlayout opgeslagen.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Diagramlayout opslaan mislukt");
    } finally {
      setDiagramSaving(false);
    }
  }

  async function handleArchiveDiagram() {
    if (!selectedDiagramId) return;

    try {
      setDiagramArchiving(true);
      await archiveAssetDiagram(selectedDiagramId, platformRequestOptions);
      setSelectedDiagramId("");
      setSuccessMessage("Diagram gearchiveerd.");
      await loadScopeData();
    } catch (err) {
      handlePageError(err, "Diagram archiveren mislukt");
    } finally {
      setDiagramArchiving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <PageHeader
          eyebrow={lockedClientId ? "Klanthub" : "Projecten"}
          title="Asset Builder"
          description="Ruimtelijk en technisch gebouwmodel wordt geladen."
        />
        <EmptyState
          title="Laden..."
          description="De Asset Builder wordt voorbereid."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={lockedClientId ? "Klanthub" : "Projecten"}
        title="Asset Builder"
        description={lockedClientId
          ? `Vaste klantcontext${lockedClientName ? `: ${lockedClientName}` : ""}. BAG/PDOK, locaties en assets blijven binnen deze klant.`
          : "BAG/PDOK vormt hier de ruimtelijke basis; installaties en assets blijven aparte technische domeinen."}
        meta={
          <>
            {isRootMode ? (
              <StatusBadge
                label={selectedPlatformTenant?.companyName || "Root tenantmodus"}
                tone="warning"
              />
            ) : null}
            {lockedClientId ? <StatusBadge label={lockedClientName || "Geselecteerde klant"} tone="info" /> : null}
            <StatusBadge label={`${buildings.length} gebouwen`} />
            <StatusBadge label={`${visibleAssets.length} assets in beeld`} tone="info" />
            <StatusBadge label={`${connections.length} verbindingen`} tone="success" />
          </>
        }
        actions={
          <>
            <Button
              tone="secondary"
              onClick={loadScopeData}
              disabled={!selectedClientId || scopeLoading || (isRootMode && !selectedPlatformTenantId)}
            >
              Verversen
            </Button>
            {canEditDiagrams ? (
              <Button
                tone="primary"
                onClick={handleSaveDiagram}
                disabled={!selectedClientId || diagramSaving || (isRootMode && !selectedPlatformTenantId)}
              >
                {diagramSaving ? "Opslaan..." : "Layout opslaan"}
              </Button>
            ) : null}
          </>
        }
      />

      {error ? <EmptyState title="Actie vereist" description={error} /> : null}
      {successMessage ? (
        <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {successMessage}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        <div className="space-y-6">
          <SectionPanel
            title="Scope"
            description={lockedClientId
              ? "De klant is vastgezet vanuit de Klanthub. Locatie, gebouw, verdieping en installatie verfijnen de context."
              : "Locatie, gebouw, verdieping en installatie bepalen samen de builder-context."}
          >
            <div className="space-y-4">
              {isRootMode ? (
                <FormField label="Tenant">
                  <Select
                    value={selectedPlatformTenantId}
                    onChange={(event) => {
                      const nextTenantId = event.target.value;
                      setSelectedPlatformTenantId(nextTenantId);
                      writeStoredPlatformTenantId(nextTenantId);
                    }}
                  >
                    <option value="">Kies een tenant</option>
                    {platformTenants.map((tenant) => (
                      <option key={asId(tenant?.tenantId)} value={asId(tenant?.tenantId)}>
                        {tenant?.companyName || tenant?.tenantId || "Naamloze tenant"}
                      </option>
                    ))}
                  </Select>
                </FormField>
              ) : null}

              {lockedClientId ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span className="block text-xs font-medium uppercase tracking-wide text-slate-500">Klant</span>
                  <span className="font-medium text-slate-900">
                    {selectedClient?.naam || selectedClient?.name || lockedClientName || "Geselecteerde klant"}
                  </span>
                </div>
              ) : (
              <FormField label="Klant">
                <Select
                  value={selectedClientId}
                  onChange={(event) => {
                    setSelectedClientId(event.target.value);
                    setSelectedLocationId("");
                    setSelectedBuildingId("");
                    setSelectedLevelId("");
                    setSelectedAssetId("");
                    setSelectedDiagramId("");
                  }}
                  disabled={isRootMode && !selectedPlatformTenantId}
                >
                  <option value="">Kies een klant</option>
                  {clients.map((client) => (
                    <option key={asId(client?._id)} value={asId(client?._id)}>
                      {client?.naam || client?.name || "Onbekende klant"}
                    </option>
                  ))}
                </Select>
              </FormField>
              )}

              <FormField label="Locatie">
                <Select
                  value={selectedLocationId}
                  onChange={(event) => {
                    setSelectedLocationId(event.target.value);
                    setSelectedBuildingId("");
                    setSelectedLevelId("");
                    setSelectedTechnicalInstallationId("");
                    setSelectedAssetId("");
                    setSelectedDiagramId("");
                    setSelectedDiagramType("site");
                  }}
                  disabled={isRootMode && !selectedPlatformTenantId}
                >
                  <option value="">Kies een locatie</option>
                  {locationOptions.map((project) => (
                    <option key={asId(project?._id)} value={asId(project?._id)}>
                      {project?.naam || project?.title || "Naamloos project"}
                    </option>
                  ))}
                </Select>
              </FormField>

              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Assetstatus">
                  <Select
                    value={assetArchiveFilter}
                    onChange={(event) => setAssetArchiveFilter(event.target.value)}
                  >
                    <option value="active">Actieve assets</option>
                    <option value="archived">Gearchiveerde assets</option>
                  </Select>
                </FormField>

                <FormField label="Assettype">
                  <Select
                    value={assetTypeFilter}
                    onChange={(event) => setAssetTypeFilter(event.target.value)}
                  >
                    <option value="">Alle typen</option>
                    {TECHNICAL_ASSET_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>

              <FormField label="Gebouw">
                <Select
                  value={selectedBuildingId}
                  onChange={(event) => {
                    setSelectedBuildingId(event.target.value);
                    setSelectedLevelId("");
                    setSelectedDiagramId("");
                  }}
                  disabled={!selectedLocationId}
                >
                  <option value="">Geen gebouwselectie</option>
                  {buildings.map((building) => (
                    <option key={asId(building?._id)} value={asId(building?._id)}>
                      {building?.name || "Naamloos gebouw"}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Installatie">
                <Select
                  value={selectedTechnicalInstallationId}
                  onChange={(event) => setSelectedTechnicalInstallationId(event.target.value)}
                  disabled={!selectedLocationId}
                >
                  <option value="">Alle installaties</option>
                  {technicalInstallationsInScope.map((installation) => (
                    <option key={asId(installation?._id)} value={asId(installation?._id)}>
                      {[installation?.code || "", installation?.name || "Naamloze installatie"]
                        .filter(Boolean)
                        .join(" · ")}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Locatienode">
                <Select
                  value={selectedLocationNodeId}
                  onChange={(event) => setSelectedLocationNodeId(event.target.value)}
                  disabled={!selectedClientId}
                >
                  <option value="">Alle locatienodes</option>
                  {scopedLocationNodes.map((node) => (
                    <option key={asId(node?._id)} value={asId(node?._id)}>
                      {formatLocationNodeLabel(node)}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Zoeken">
                <TextInput
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Zoek op tag, naam, model of serienummer"
                />
              </FormField>

              <FormField label="Diagramnaam">
                <TextInput
                  value={diagramName}
                  onChange={(event) => setDiagramName(event.target.value)}
                  placeholder="Automatische diagramnaam"
                />
              </FormField>

              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  Weergave
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["site", "vertical"].map((type) => (
                    <Button
                      key={type}
                      size="sm"
                      tone={selectedDiagramType === type ? "primary" : "secondary"}
                      onClick={() => {
                        setSelectedDiagramType(type);
                        if (type !== "floor") setSelectedLevelId("");
                        setSelectedDiagramId("");
                      }}
                      disabled={type !== "site" && !selectedBuildingId}
                    >
                      {findOptionLabel(TECHNICAL_DIAGRAM_TYPE_OPTIONS, type)}
                    </Button>
                  ))}
                  {levelsForBuilding.map((level) => (
                    <Button
                      key={asId(level?._id)}
                      size="sm"
                      tone={
                        selectedDiagramType === "floor" &&
                        asId(level?._id) === selectedLevelId
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => {
                        setSelectedDiagramType("floor");
                        setSelectedLevelId(asId(level?._id));
                        setSelectedDiagramId("");
                      }}
                    >
                      {level?.code || level?.name || "Verdieping"}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </SectionPanel>

          <SectionPanel
            title="BAG/PDOK"
            description="Verrijk de locatie met officiële adresdata, panden en contouren."
          >
            {!selectedLocationId ? (
              <EmptyState
                title="Kies eerst een locatie"
                description="De verrijking hangt aan een bestaande Logboek24-locatie."
              />
            ) : (
              <div className="space-y-4">
                <FormField label="Zoek adres">
                  <div className="flex gap-2">
                    <TextInput
                      value={bagQuery}
                      onChange={(event) => setBagQuery(event.target.value)}
                      placeholder="Stationsstraat 12, Rotterdam"
                    />
                    {canConfigureAssets ? (
                      <Button
                        tone="secondary"
                        onClick={handleSearchBag}
                        disabled={bagSearching || !bagQuery.trim()}
                      >
                        {bagSearching ? "Zoeken..." : "Zoek"}
                      </Button>
                    ) : null}
                  </div>
                </FormField>

                {bagResults.length > 0 ? (
                  <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                    {bagResults.map((result) => (
                      <button
                        key={String(result?.id || Math.random())}
                        type="button"
                        onClick={() => handleSelectBagResult(result)}
                        className="w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:border-slate-300"
                      >
                        {result?.omschrijving || result?.formattedAddress || result?.id}
                      </button>
                    ))}
                  </div>
                ) : null}

                {selectedBagAddress ? (
                  <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <p className="font-medium text-slate-900">
                      {selectedBagAddress?.formattedAddress || "Adres"}
                    </p>
                    <p>{selectedBagAddress?.woonplaatsNaam || "-"}</p>
                    <p className="text-xs text-slate-500">
                      Panden:{" "}
                      {Array.isArray(selectedBagAddress?.pandIdentificaties) &&
                      selectedBagAddress.pandIdentificaties.length > 0
                        ? selectedBagAddress.pandIdentificaties.join(", ")
                        : "geen pand-ID beschikbaar"}
                    </p>
                    <div className="mt-3">
                      {canConfigureAssets ? (
                        <Button
                          tone="primary"
                          onClick={handleEnrichLocation}
                          disabled={enrichingLocation}
                        >
                          {enrichingLocation ? "Verrijken..." : "Locatie verrijken"}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </SectionPanel>

          <SectionPanel
            title="Gebouwstructuur"
            description="Voeg handmatig gebouwen en verdiepingen toe naast BAG-voorstellen."
          >
            <div className="space-y-6">
              <form className="space-y-3" onSubmit={handleCreateBuilding}>
                <FormField label="Nieuw gebouw">
                  <TextInput
                    value={buildingForm.name}
                    onChange={(event) =>
                      setBuildingForm((current) => ({ ...current, name: event.target.value }))
                    }
                    placeholder="Garage of bijgebouw"
                  />
                </FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Type">
                    <Select
                      value={buildingForm.type}
                      onChange={(event) =>
                        setBuildingForm((current) => ({ ...current, type: event.target.value }))
                      }
                    >
                      {SITE_BUILDING_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField label="Code">
                    <TextInput
                      value={buildingForm.code}
                      onChange={(event) =>
                        setBuildingForm((current) => ({ ...current, code: event.target.value }))
                      }
                      placeholder="GB-A"
                    />
                  </FormField>
                </div>
                {canConfigureAssets ? (
                  <Button
                    type="submit"
                    tone="secondary"
                    disabled={!selectedLocationId || buildingSaving || !buildingForm.name.trim()}
                  >
                    {buildingSaving ? "Opslaan..." : "Gebouw toevoegen"}
                  </Button>
                ) : null}
              </form>

              <form className="space-y-3" onSubmit={handleCreateLevel}>
                <FormField label="Nieuwe verdieping">
                  <TextInput
                    value={levelForm.name}
                    onChange={(event) =>
                      setLevelForm((current) => ({ ...current, name: event.target.value }))
                    }
                    placeholder="Begane grond"
                  />
                </FormField>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Type">
                    <Select
                      value={levelForm.type}
                      onChange={(event) =>
                        setLevelForm((current) => ({ ...current, type: event.target.value }))
                      }
                    >
                      {BUILDING_LEVEL_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField label="Aanmaakmodus">
                    <Select
                      value={levelForm.createMode}
                      onChange={(event) =>
                        setLevelForm((current) => ({
                          ...current,
                          createMode: event.target.value,
                        }))
                      }
                    >
                      {BUILDING_LEVEL_CREATE_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Volgnummer">
                    <TextInput
                      type="number"
                      value={levelForm.levelNumber}
                      onChange={(event) =>
                        setLevelForm((current) => ({
                          ...current,
                          levelNumber: event.target.value,
                        }))
                      }
                      placeholder="0"
                    />
                  </FormField>
                  <FormField label="Sortering">
                    <TextInput
                      type="number"
                      value={levelForm.sortOrder}
                      onChange={(event) =>
                        setLevelForm((current) => ({
                          ...current,
                          sortOrder: event.target.value,
                        }))
                      }
                      placeholder="0"
                    />
                  </FormField>
                </div>
                {canConfigureAssets ? (
                  <Button
                    type="submit"
                    tone="secondary"
                    disabled={!selectedBuildingId || levelSaving || !levelForm.name.trim()}
                  >
                    {levelSaving ? "Opslaan..." : "Verdieping toevoegen"}
                  </Button>
                ) : null}
              </form>
            </div>
          </SectionPanel>

          <SectionPanel
            title="Technische installatie"
            description="Installaties blijven aparte technische systemen en hangen aan locatie, gebouw en optioneel verdieping."
          >
            <form className="space-y-3" onSubmit={handleCreateTechnicalInstallation}>
              <FormField label="Naam">
                <TextInput
                  value={installationForm.name}
                  onChange={(event) =>
                    setInstallationForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Elektrotechnische installatie"
                />
              </FormField>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Type">
                  <Select
                    value={installationForm.installationType}
                    onChange={(event) =>
                      setInstallationForm((current) => ({
                        ...current,
                        installationType: event.target.value,
                      }))
                    }
                  >
                    {TECHNICAL_INSTALLATION_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Code">
                  <TextInput
                    value={installationForm.code}
                    onChange={(event) =>
                      setInstallationForm((current) => ({
                        ...current,
                        code: event.target.value,
                      }))
                    }
                    placeholder="EI-01"
                  />
                </FormField>
              </div>
              <FormField label="Beschrijving">
                <Textarea
                  rows={3}
                  value={installationForm.description}
                  onChange={(event) =>
                    setInstallationForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </FormField>
              {canConfigureAssets ? (
                <Button
                  type="submit"
                  tone="secondary"
                  disabled={!selectedLocationId || installationSaving || !installationForm.name.trim()}
                >
                  {installationSaving ? "Opslaan..." : "Installatie toevoegen"}
                </Button>
              ) : null}
            </form>
          </SectionPanel>

          <SectionPanel
            title="Nieuw asset"
            description="Assets verwijzen afzonderlijk naar hun ruimtelijke positie en naar hun technische installatie."
          >
            <form className="space-y-4" onSubmit={handleCreateAsset}>
              <FormField label="Naam">
                <TextInput
                  value={assetForm.name}
                  onChange={(event) =>
                    setAssetForm((current) => ({ ...current, name: event.target.value }))
                  }
                  placeholder="Onderverdeler BG"
                />
              </FormField>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Assettype">
                  <Select
                    value={assetForm.assetType}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        assetType: event.target.value,
                      }))
                    }
                  >
                    {TECHNICAL_ASSET_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Status">
                  <Select
                    value={assetForm.status}
                    onChange={(event) =>
                      setAssetForm((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                  >
                    {TECHNICAL_ASSET_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
              <FormField
                label="Asset-installatietype"
                hint="Wordt automatisch afgeleid uit de gekozen technische installatie wanneer aanwezig."
              >
                <Select
                  value={assetForm.installationType}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      installationType: event.target.value,
                    }))
                  }
                >
                  {TECHNICAL_ASSET_INSTALLATION_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Beschrijving">
                <Textarea
                  rows={3}
                  value={assetForm.description}
                  onChange={(event) =>
                    setAssetForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Optionele context voor plaatsing of montage."
                />
              </FormField>

              {isDistributionBoardAssetType(assetForm.assetType) ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormField label="Fasen">
                    <TextInput
                      value={assetForm.technicalSpecs.phases}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            phases: event.target.value,
                          },
                        }))
                      }
                      placeholder="3~"
                    />
                  </FormField>
                  <FormField label="Nominale spanning">
                    <TextInput
                      type="number"
                      value={assetForm.technicalSpecs.nominalVoltage}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            nominalVoltage: event.target.value,
                          },
                        }))
                      }
                      placeholder="400"
                    />
                  </FormField>
                  <FormField label="Hoofdzekering (A)">
                    <TextInput
                      type="number"
                      value={assetForm.technicalSpecs.mainFuseAmps}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            mainFuseAmps: event.target.value,
                          },
                        }))
                      }
                      placeholder="160"
                    />
                  </FormField>
                  <FormField label="Nominale stroom (A)">
                    <TextInput
                      type="number"
                      value={assetForm.technicalSpecs.ratedCurrentAmps}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            ratedCurrentAmps: event.target.value,
                          },
                        }))
                      }
                      placeholder="125"
                    />
                  </FormField>
                  <FormField label="Totaal modules">
                    <TextInput
                      type="number"
                      value={assetForm.technicalSpecs.totalModules}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            totalModules: event.target.value,
                          },
                        }))
                      }
                      placeholder="36"
                    />
                  </FormField>
                  <FormField label="IP-waarde">
                    <TextInput
                      value={assetForm.technicalSpecs.ipRating}
                      onChange={(event) =>
                        setAssetForm((current) => ({
                          ...current,
                          technicalSpecs: {
                            ...current.technicalSpecs,
                            ipRating: event.target.value,
                          },
                        }))
                      }
                      placeholder="IP54"
                    />
                  </FormField>
                </div>
              ) : null}

              <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Voorgestelde tag:{" "}
                <span className="font-semibold text-slate-900">
                  {tagPreview || "Nog geen preview beschikbaar"}
                </span>
              </div>

              {canCreateAssets ? (
                <Button
                  type="submit"
                  tone="primary"
                  disabled={!selectedClientId || assetSaving || !assetForm.name.trim()}
                >
                  {assetSaving ? "Opslaan..." : "Asset aanmaken"}
                </Button>
              ) : null}
            </form>
          </SectionPanel>

          <SectionPanel
            title="Nieuwe verbinding"
            description="Ondersteunt ook cross-level verbindingen; het diagram maakt daar waar nodig overgangsnodes van."
          >
            <form className="space-y-4" onSubmit={handleCreateConnection}>
              <FormField label="Bronasset">
                <Select
                  value={connectionForm.sourceAssetId}
                  onChange={(event) =>
                    setConnectionForm((current) => ({
                      ...current,
                      sourceAssetId: event.target.value,
                    }))
                  }
                >
                  <option value="">Kies een bronasset</option>
                  {assets.map((asset) => (
                    <option key={asId(asset?._id)} value={asId(asset?._id)}>
                      {formatAssetLabel(asset)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Doelasset">
                <Select
                  value={connectionForm.targetAssetId}
                  onChange={(event) =>
                    setConnectionForm((current) => ({
                      ...current,
                      targetAssetId: event.target.value,
                    }))
                  }
                >
                  <option value="">Kies een doelasset</option>
                  {assets.map((asset) => (
                    <option key={asId(asset?._id)} value={asId(asset?._id)}>
                      {formatAssetLabel(asset)}
                    </option>
                  ))}
                </Select>
              </FormField>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Verbindingstype">
                  <Select
                    value={connectionForm.connectionType}
                    onChange={(event) =>
                      setConnectionForm((current) => ({
                        ...current,
                        connectionType: event.target.value,
                      }))
                    }
                  >
                    {TECHNICAL_CONNECTION_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Label">
                  <TextInput
                    value={connectionForm.label}
                    onChange={(event) =>
                      setConnectionForm((current) => ({
                        ...current,
                        label: event.target.value,
                      }))
                    }
                    placeholder="Voeding naar 1e verdieping"
                  />
                </FormField>
              </div>
              {canManageConnections ? (
                <Button
                  type="submit"
                  tone="secondary"
                  disabled={
                    !selectedClientId ||
                    connectionSaving ||
                    !connectionForm.sourceAssetId ||
                    !connectionForm.targetAssetId
                  }
                >
                  {connectionSaving ? "Opslaan..." : "Verbinding aanmaken"}
                </Button>
              ) : null}
            </form>
          </SectionPanel>
        </div>

        <div className="space-y-6">
          <SectionPanel
            title="Diagramcanvas"
            description="React Flow toont terrein, verdieping of verticale doorsnede binnen dezelfde asset- en verbindingstructuur."
            actions={
              <div className="flex items-center gap-2">
              <Select
                className="min-w-[260px]"
                value={selectedDiagramId}
                onChange={(event) => setSelectedDiagramId(event.target.value)}
                disabled={!selectedClientId || !canViewDiagrams}
              >
                <option value="">Geen bestaand diagram</option>
                {diagrams.map((diagram) => (
                  <option key={asId(diagram?._id)} value={asId(diagram?._id)}>
                    {diagram?.name || "Naamloos diagram"}
                  </option>
                ))}
              </Select>
              {canEditDiagrams && selectedDiagramId ? (
                <Button
                  size="sm"
                  tone="danger"
                  onClick={handleArchiveDiagram}
                  disabled={diagramArchiving}
                >
                  {diagramArchiving ? "Archiveren..." : "Diagram archiveren"}
                </Button>
              ) : null}
              </div>
            }
            contentClassName="space-y-4"
          >
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="min-h-[700px] overflow-hidden rounded-sm border border-slate-200 bg-[radial-gradient(circle_at_top,_rgba(226,232,240,0.8),_rgba(248,250,252,0.95)_45%,_rgba(255,255,255,1)_100%)]">
                {!selectedLocationId ? (
                  <div className="flex h-full items-center justify-center p-8">
                    <EmptyState
                      title="Kies eerst een locatie"
                      description="De ruimtelijke builder start zodra een klantlocatie is gekozen."
                    />
                  </div>
                ) : (
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onInit={(instance) => {
                      reactFlowRef.current = instance;
                      const viewport = selectedDiagram?.viewport;
                      if (viewport) {
                        window.setTimeout(() => {
                          instance.setViewport(
                            {
                              x: Number(viewport?.x || 0),
                              y: Number(viewport?.y || 0),
                              zoom: Number(viewport?.zoom || 1),
                            },
                            { duration: 250 }
                          );
                        }, 0);
                      }
                    }}
                    onNodeClick={(_event, node) => {
                      if (String(node?.id || "").startsWith("transfer:")) {
                        if (node?.data?.targetLevelId) {
                          setSelectedDiagramType("floor");
                          setSelectedLevelId(String(node.data.targetLevelId));
                          setSelectedDiagramId("");
                        }
                        return;
                      }

                      if (String(node?.id || "").startsWith("level:")) {
                        if (node?.data?.levelId) {
                          setSelectedDiagramType("floor");
                          setSelectedLevelId(String(node.data.levelId));
                          setSelectedDiagramId("");
                        }
                        return;
                      }

                      setSelectedAssetId(node.id);
                    }}
                    fitView
                    minZoom={0.1}
                    maxZoom={2.5}
                    className="bg-transparent"
                  >
                    <MiniMap
                      pannable
                      zoomable
                      nodeColor={(node) => {
                        if (String(node?.id || "").includes(":")) return "#cbd5e1";
                        return node.id === selectedAssetId ? "#0f172a" : "#64748b";
                      }}
                    />
                    <Controls />
                    <Background gap={24} size={1} color="#cbd5e1" />
                  </ReactFlow>
                )}
              </div>

              <div className="space-y-4">
                <SectionPanel
                  title="Scope-overzicht"
                  description="Snelle controle op welke locatie- en gebouwlaag deze layout hoort."
                >
                  <dl className="space-y-2 text-sm text-slate-700">
                    <div>
                      <dt className="font-medium text-slate-900">Tenant</dt>
                      <dd>
                        {selectedPlatformTenant?.companyName ||
                          (isRootMode ? "Geen tenant geselecteerd" : "Huidige tenant")}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-900">Klant</dt>
                      <dd>{selectedClient?.naam || "-"}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-900">Locatie</dt>
                      <dd>{selectedLocation?.naam || selectedLocation?.title || "-"}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-900">Gebouw</dt>
                      <dd>{selectedBuilding?.name || "Niet geselecteerd"}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-900">Verdieping</dt>
                      <dd>{selectedLevel?.name || "Niet geselecteerd"}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-900">Weergave</dt>
                      <dd>{findOptionLabel(TECHNICAL_DIAGRAM_TYPE_OPTIONS, selectedDiagramType)}</dd>
                    </div>
                  </dl>
                </SectionPanel>

                <SectionPanel
                  title="Gebouwen"
                  description="BAG-pandselecties kunnen per gebouw bevestigd of genegeerd worden."
                >
                  {buildings.length === 0 ? (
                    <EmptyState
                      title="Nog geen gebouwen"
                      description="Verrijk eerst een locatie of voeg handmatig een gebouw toe."
                    />
                  ) : (
                    <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1">
                      {buildings.map((building) => {
                        const buildingId = asId(building?._id);
                        const active = buildingId === selectedBuildingId;
                        return (
                          <div
                            key={buildingId}
                            className={`rounded-sm border px-3 py-3 ${
                              active
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 bg-white text-slate-800"
                            }`}
                          >
                            <button
                              type="button"
                              className="w-full text-left"
                              onClick={() => setSelectedBuildingId(buildingId)}
                            >
                              <p className="text-sm font-medium">
                                {building?.name || "Naamloos gebouw"}
                              </p>
                              <p className={`text-xs ${active ? "text-slate-200" : "text-slate-500"}`}>
                                {findOptionLabel(SITE_BUILDING_TYPE_OPTIONS, building?.type)} ·{" "}
                                {building?.bagPandId || "handmatig"}
                              </p>
                            </button>
                            {canConfigureAssets ? (
                              <div className="mt-3 flex gap-2">
                                <Button
                                  size="sm"
                                  tone={building?.selectionStatus === "confirmed" ? "primary" : "secondary"}
                                  onClick={() =>
                                    handleUpdateBuildingSelection(buildingId, "confirmed")
                                  }
                                >
                                  Bevestig
                                </Button>
                                <Button
                                  size="sm"
                                  tone={building?.selectionStatus === "ignored" ? "danger" : "secondary"}
                                  onClick={() =>
                                    handleUpdateBuildingSelection(buildingId, "ignored")
                                  }
                                >
                                  Negeer
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </SectionPanel>

                <SectionPanel
                  title="Assets in beeld"
                  description={diagramScopeDescriptor}
                >
                  {visibleAssets.length === 0 ? (
                    <EmptyState
                      title="Geen assets in beeld"
                      description="Maak een asset aan of kies een bredere scope."
                    />
                  ) : (
                    <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
                      {visibleAssets.map((asset) => {
                        const assetId = asId(asset?._id);
                        const active = assetId === selectedAssetId;
                        return (
                          <button
                            key={assetId}
                            type="button"
                            onClick={() => setSelectedAssetId(assetId)}
                            className={`w-full rounded-sm border px-3 py-2 text-left ${
                              active
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 bg-white text-slate-800"
                            }`}
                          >
                            <p className="text-sm font-medium">
                              {asset?.name || "Naamloos asset"}
                            </p>
                            <p className={`text-xs ${active ? "text-slate-200" : "text-slate-500"}`}>
                              {asset?.assetTag || "Geen tag"} ·{" "}
                              {findOptionLabel(TECHNICAL_ASSET_TYPE_OPTIONS, asset?.assetType)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </SectionPanel>

                <SectionPanel
                  title="Geselecteerd asset"
                  description="Actieve verbindingen en ruimtelijke context komen uit dezelfde assetbron."
                >
                  {!selectedAsset ? (
                    <EmptyState
                      title="Geen asset geselecteerd"
                      description="Klik een node in het diagram of kies een asset uit de lijst."
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <p className="text-base font-semibold text-slate-900">
                          {selectedAsset?.name || "Naamloos asset"}
                        </p>
                        <p className="text-sm text-slate-600">
                          {selectedAsset?.assetTag || "Geen tag"} ·{" "}
                          {findOptionLabel(TECHNICAL_ASSET_TYPE_OPTIONS, selectedAsset?.assetType)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {findBuildingName(buildings, selectedAsset?.buildingId) || "Geen gebouw"} ·{" "}
                          {findLevelName(buildingLevels, selectedAsset?.buildingLevelId) || "Geen verdieping"}
                        </p>
                      </div>

                      {selectedAssetConnections.length === 0 ? (
                        <EmptyState
                          title="Geen actieve verbindingen"
                          description="Cross-level verbindingen verschijnen in het verticale diagram en als overgangsnode op verdiepingniveau."
                        />
                      ) : (
                        <div className="space-y-2">
                          {selectedAssetConnections.map((connection) => {
                            const connectionId = asId(connection?._id);
                            const peerAssetId =
                              asId(connection?.sourceAssetId) === selectedAssetId
                                ? asId(connection?.targetAssetId)
                                : asId(connection?.sourceAssetId);
                            const peerAsset = assets.find(
                              (asset) => asId(asset?._id) === peerAssetId
                            );
                            return (
                              <div
                                key={connectionId}
                                className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-3"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">
                                      {findOptionLabel(
                                        TECHNICAL_CONNECTION_TYPE_OPTIONS,
                                        connection?.connectionType
                                      )}
                                    </p>
                                    <p className="text-xs text-slate-600">
                                      Naar: {peerAsset?.name || "Onbekend asset"}
                                    </p>
                                  </div>
                                  {canManageConnections ? (
                                    <Button
                                      size="sm"
                                      tone="secondary"
                                      disabled={archivingConnectionId === connectionId}
                                      onClick={() => handleArchiveConnection(connectionId)}
                                    >
                                      {archivingConnectionId === connectionId ? "Bezig..." : "Archiveer"}
                                    </Button>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </SectionPanel>
              </div>
            </div>
          </SectionPanel>

          {isSelectedBoard ? (
            <SectionPanel
              title="Verdeelkastgroepen"
              description="Groepen zijn afzonderlijke records; capaciteit wordt uit die onderliggende records berekend."
            >
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-5">
                    <SummaryCard label="In gebruik" value={boardSummary?.inUseModules} />
                    <SummaryCard label="Gereserveerd" value={boardSummary?.reservedModules} />
                    <SummaryCard label="Vrij" value={boardSummary?.freeModules} />
                    <SummaryCard label="Buiten gebruik" value={boardSummary?.outOfServiceModules} />
                    <SummaryCard
                      label="Bezetting"
                      value={
                        boardSummary?.occupancyRate !== null &&
                        boardSummary?.occupancyRate !== undefined
                          ? `${boardSummary.occupancyRate}%`
                          : "-"
                      }
                    />
                  </div>

                  {boardCircuits.length === 0 ? (
                    <EmptyState
                      title="Nog geen groepen"
                      description="Voeg de eerste groep of vrije positie toe aan deze verdeelkast."
                    />
                  ) : (
                    <div className="space-y-2">
                      {boardCircuits.map((circuit) => {
                        const circuitId = asId(circuit?._id);
                        return (
                          <div
                            key={circuitId}
                            className="rounded-sm border border-slate-200 bg-white px-4 py-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-slate-900">
                                  {circuit?.groupNumber || `${circuit?.positionFrom || "?"}-${circuit?.positionTo || "?"}`}
                                  {" · "}
                                  {circuit?.name || "Naamloze groep"}
                                </p>
                                <p className="text-xs text-slate-600">
                                  {findOptionLabel(
                                    DISTRIBUTION_BOARD_CIRCUIT_STATUS_OPTIONS,
                                    circuit?.status
                                  )}{" "}
                                  · {Number(circuit?.modulesUsed || 0)} modules ·{" "}
                                  {findOptionLabel(
                                    DISTRIBUTION_BOARD_CIRCUIT_PHASE_OPTIONS,
                                    circuit?.phase
                                  )}
                                </p>
                              </div>
                              {canEditBoardCircuits ? (
                                <Button
                                  size="sm"
                                  tone="secondary"
                                  disabled={archivingCircuitId === circuitId}
                                  onClick={() => handleArchiveCircuit(circuitId)}
                                >
                                  {archivingCircuitId === circuitId ? "Bezig..." : "Archiveer"}
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <form className="space-y-3" onSubmit={handleCreateCircuit}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Groepnummer">
                      <TextInput
                        value={circuitForm.groupNumber}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            groupNumber: event.target.value,
                          }))
                        }
                        placeholder="G12"
                      />
                    </FormField>
                    <FormField label="Naam">
                      <TextInput
                        value={circuitForm.name}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                        placeholder="Verlichting gang"
                      />
                    </FormField>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <FormField label="Van positie">
                      <TextInput
                        type="number"
                        value={circuitForm.positionFrom}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            positionFrom: event.target.value,
                          }))
                        }
                      />
                    </FormField>
                    <FormField label="Tot positie">
                      <TextInput
                        type="number"
                        value={circuitForm.positionTo}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            positionTo: event.target.value,
                          }))
                        }
                      />
                    </FormField>
                    <FormField label="Modules">
                      <TextInput
                        type="number"
                        value={circuitForm.modulesUsed}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            modulesUsed: event.target.value,
                          }))
                        }
                      />
                    </FormField>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Status">
                      <Select
                        value={circuitForm.status}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            status: event.target.value,
                          }))
                        }
                      >
                        {DISTRIBUTION_BOARD_CIRCUIT_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                    <FormField label="Fase">
                      <Select
                        value={circuitForm.phase}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            phase: event.target.value,
                          }))
                        }
                      >
                        {DISTRIBUTION_BOARD_CIRCUIT_PHASE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Automaat type">
                      <TextInput
                        value={circuitForm.breakerType}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            breakerType: event.target.value,
                          }))
                        }
                        placeholder="Installatieautomaat"
                      />
                    </FormField>
                    <FormField label="Automaat ampère">
                      <TextInput
                        type="number"
                        value={circuitForm.breakerAmps}
                        onChange={(event) =>
                          setCircuitForm((current) => ({
                            ...current,
                            breakerAmps: event.target.value,
                          }))
                        }
                        placeholder="16"
                      />
                    </FormField>
                  </div>
                  <FormField label="Beschrijving">
                    <Textarea
                      rows={3}
                      value={circuitForm.description}
                      onChange={(event) =>
                        setCircuitForm((current) => ({
                          ...current,
                          description: event.target.value,
                        }))
                      }
                    />
                  </FormField>
                  {canEditBoardCircuits ? (
                    <Button
                      type="submit"
                      tone="primary"
                      disabled={circuitSaving}
                    >
                      {circuitSaving ? "Opslaan..." : "Groep toevoegen"}
                    </Button>
                  ) : null}
                </form>
              </div>
            </SectionPanel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-3">
      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{value ?? 0}</p>
    </div>
  );
}

function buildFlowGraph({
  diagramType,
  visibleAssets,
  allAssets,
  connections,
  selectedDiagram,
  buildings,
  levels,
  allLevels,
  selectedBuilding,
  selectedLevel,
}) {
  const savedPositions = new Map(
    (selectedDiagram?.assetPositions || []).map((item) => [asId(item?.assetId), item])
  );
  const assetMap = new Map(allAssets.map((asset) => [asId(asset?._id), asset]));
  const nodes = [];
  const edges = [];
  const assetNodes = [];
  let backgroundPayload = null;
  const buildingAnchors = new Map();
  const levelAnchors = new Map();

  if (diagramType === "site") {
    const buildingScaffold = buildSiteScaffold(buildings);
    nodes.push(...buildingScaffold.nodes);
    backgroundPayload = buildingScaffold.backgroundPayload;
    for (const [buildingId, anchor] of buildingScaffold.anchors.entries()) {
      buildingAnchors.set(buildingId, anchor);
    }
  }

  if (diagramType === "floor") {
    const floorScaffold = buildFloorScaffold(selectedBuilding, selectedLevel);
    nodes.push(...floorScaffold.nodes);
    backgroundPayload = floorScaffold.backgroundPayload;
  }

  if (diagramType === "vertical") {
    const verticalScaffold = buildVerticalScaffold(levels);
    nodes.push(...verticalScaffold.nodes);
    for (const [levelId, anchor] of verticalScaffold.anchors.entries()) {
      levelAnchors.set(levelId, anchor);
    }
  }

  const levelCounters = new Map();
  const buildingCounters = new Map();

  for (let index = 0; index < visibleAssets.length; index += 1) {
    const asset = visibleAssets[index];
    const assetId = asId(asset?._id);
    const saved = savedPositions.get(assetId);
    let fallback = getGridPosition(index);

    if (diagramType === "site" && buildingAnchors.has(asId(asset?.buildingId))) {
      const buildingId = asId(asset?.buildingId);
      const count = buildingCounters.get(buildingId) || 0;
      const anchor = buildingAnchors.get(buildingId);
      fallback = {
        x: anchor.x + (count % 2) * 150 - 60,
        y: anchor.y + Math.floor(count / 2) * 100 - 30,
      };
      buildingCounters.set(buildingId, count + 1);
    }

    if (diagramType === "vertical") {
      const levelId = asId(asset?.buildingLevelId);
      const anchor = levelAnchors.get(levelId) || {
        x: 360,
        y: 100 + levels.length * 180,
      };
      const count = levelCounters.get(levelId || "none") || 0;
      fallback = {
        x: anchor.x + (count % 3) * 230,
        y: anchor.y + Math.floor(count / 3) * 110,
      };
      levelCounters.set(levelId || "none", count + 1);
    }

    assetNodes.push({
      id: assetId,
      position: {
        x: Number(saved?.x ?? fallback.x),
        y: Number(saved?.y ?? fallback.y),
      },
      data: {
        label: renderAssetNode(asset),
      },
      style: {
        background: "transparent",
        border: "none",
        width: 220,
      },
      draggable: true,
    });
  }

  nodes.push(...assetNodes);

  if (diagramType === "floor") {
    const transferNodes = buildFloorTransferNodes({
      connections,
      assetMap,
      visibleAssetIds: new Set(visibleAssets.map((asset) => asId(asset?._id))),
      allLevels,
      assetNodes,
    });
    nodes.push(...transferNodes.nodes);
    edges.push(...transferNodes.edges);
  }

  edges.push(
    ...buildFlowEdges({
      connections,
      assets: visibleAssets,
      allAssets,
      diagramType,
    })
  );

  return {
    nodes,
    edges,
    backgroundPayload,
  };
}

function buildSiteScaffold(buildings) {
  const featureCollection = {
    type: "FeatureCollection",
    features: buildings
      .filter((building) => building?.geometry || building?.sourceGeometry)
      .map((building) => ({
        type: "Feature",
        properties: {
          buildingId: asId(building?._id),
          name: building?.name || "Gebouw",
        },
        geometry: building?.geometry || building?.sourceGeometry,
      })),
  };

  const normalized = normalizeGeoGeometry(featureCollection, {
    targetWidth: 900,
    targetHeight: 620,
    padding: 40,
  });
  const anchors = new Map();
  const nodes = normalized.features.map((feature) => {
    const bounds = feature.bounds || {};
    const width = Math.max(Number(bounds.maxX - bounds.minX || 0), 120);
    const height = Math.max(Number(bounds.maxY - bounds.minY || 0), 90);
    const x = Number(bounds.minX || 0);
    const y = Number(bounds.minY || 0);
    const buildingId = String(feature?.properties?.buildingId || "");
    anchors.set(buildingId, {
      x: x + width / 2,
      y,
    });
    return {
      id: `building:${buildingId}`,
      draggable: false,
      selectable: false,
      position: { x, y },
      style: {
        background: "transparent",
        border: "none",
        width,
        height,
      },
      data: {
        label: (
          <div className="pointer-events-none h-full w-full">
            <svg className="h-full w-full overflow-visible" viewBox={`0 0 ${width} ${height}`}>
              {geometryToSvgPaths(feature.geometry, { offsetX: x, offsetY: y }).map((path, index) => (
                <path
                  key={`${buildingId}-${index}`}
                  d={path}
                  fill="rgba(148, 163, 184, 0.24)"
                  stroke="#334155"
                  strokeWidth="2"
                />
              ))}
            </svg>
            <div className="mt-2 rounded-sm bg-white/90 px-2 py-1 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-slate-200">
              {feature?.properties?.name || "Gebouw"}
            </div>
          </div>
        ),
      },
    };
  });

  return {
    nodes,
    anchors,
    backgroundPayload: normalized.geometry
      ? {
          type: "geojson",
          geometry: normalized.geometry,
          sourceGeometry: featureCollection,
          opacity: 1,
          scale: normalized.transform?.scale || 1,
          rotation: 0,
          offsetX: 0,
          offsetY: 0,
          transform: normalized.transform,
        }
      : null,
  };
}

function buildFloorScaffold(selectedBuilding, selectedLevel) {
  const sourceGeometry =
    selectedLevel?.customGeometry ||
    selectedLevel?.inheritedFootprint ||
    selectedBuilding?.geometry ||
    selectedBuilding?.sourceGeometry ||
    null;

  if (!sourceGeometry) {
    return { nodes: [], backgroundPayload: null };
  }

  const normalized = normalizeGeoGeometry(sourceGeometry, {
    targetWidth: 900,
    targetHeight: 620,
    padding: 60,
  });

  const width = normalized.width;
  const height = normalized.height;

  return {
    nodes: [
      {
        id: "floor:background",
        draggable: false,
        selectable: false,
        position: { x: 0, y: 0 },
        style: {
          background: "transparent",
          border: "none",
          width,
          height,
        },
        data: {
          label: (
            <div className="pointer-events-none h-full w-full">
              <svg className="h-full w-full" viewBox={`0 0 ${width} ${height}`}>
                {normalized.features.flatMap((feature, featureIndex) =>
                  geometryToSvgPaths(feature.geometry).map((path, pathIndex) => (
                    <path
                      key={`${featureIndex}-${pathIndex}`}
                      d={path}
                      fill="rgba(226, 232, 240, 0.65)"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                  ))
                )}
              </svg>
            </div>
          ),
        },
      },
    ],
    backgroundPayload: normalized.geometry
      ? {
          type:
            String(selectedBuilding?.geometryMetadata?.geometrySource || "").trim() === "bag"
              ? "bag_polygon"
              : "geojson",
          geometry: normalized.geometry,
          sourceGeometry,
          opacity: 1,
          scale: normalized.transform?.scale || 1,
          rotation: 0,
          offsetX: 0,
          offsetY: 0,
          transform: normalized.transform,
        }
      : null,
  };
}

function buildVerticalScaffold(levels) {
  const anchors = new Map();
  const nodes = levels.map((level, index) => {
    const x = 60;
    const y = 60 + index * 200;
    const width = 220;
    const height = 140;
    const levelId = asId(level?._id);
    anchors.set(levelId, {
      x: 330,
      y: y + 20,
    });
    return {
      id: `level:${levelId}`,
      position: { x, y },
      draggable: false,
      data: {
        levelId,
        label: (
          <div className="h-full rounded-sm border border-slate-300 bg-slate-50 px-4 py-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
              Verdieping
            </p>
            <p className="mt-2 text-base font-semibold text-slate-900">
              {level?.name || "Naamloze verdieping"}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {findOptionLabel(BUILDING_LEVEL_TYPE_OPTIONS, level?.type)}
            </p>
          </div>
        ),
      },
      style: {
        width,
        height,
        border: "none",
        background: "transparent",
      },
    };
  });

  return { nodes, anchors };
}

function buildFloorTransferNodes({
  connections,
  assetMap,
  visibleAssetIds,
  allLevels,
  assetNodes,
}) {
  const nodes = [];
  const edges = [];
  const nodeMap = new Map(assetNodes.map((node) => [node.id, node]));
  let laneIndex = 0;

  for (const connection of connections) {
    const sourceAssetId = asId(connection?.sourceAssetId);
    const targetAssetId = asId(connection?.targetAssetId);
    const sourceVisible = visibleAssetIds.has(sourceAssetId);
    const targetVisible = visibleAssetIds.has(targetAssetId);
    if (sourceVisible === targetVisible) continue;

    const visibleId = sourceVisible ? sourceAssetId : targetAssetId;
    const hiddenId = sourceVisible ? targetAssetId : sourceAssetId;
    const hiddenAsset = assetMap.get(hiddenId);
    if (!hiddenAsset) continue;

    const visibleNode = nodeMap.get(visibleId);
    const targetLevelId = asId(hiddenAsset?.buildingLevelId);
    const targetLevel = allLevels.find((level) => asId(level?._id) === targetLevelId);
    const x = (visibleNode?.position?.x || 0) + 260;
    const y = (visibleNode?.position?.y || 0) + laneIndex * 22;
    laneIndex += 1;

    const transferNodeId = `transfer:${asId(connection?._id)}:${hiddenId}`;
    nodes.push({
      id: transferNodeId,
      position: { x, y },
      draggable: false,
      data: {
        targetLevelId,
        label: (
          <div className="rounded-sm border border-dashed border-slate-400 bg-white px-3 py-2 text-xs text-slate-700 shadow-sm">
            {connection?.label || "Overgang"} · {targetLevel?.name || "Andere verdieping"}
          </div>
        ),
      },
      style: {
        width: 190,
        border: "none",
        background: "transparent",
      },
    });

    edges.push({
      id: `${transferNodeId}:edge`,
      source: visibleId,
      target: transferNodeId,
      label: connection?.label || findOptionLabel(TECHNICAL_CONNECTION_TYPE_OPTIONS, connection?.connectionType),
      style: {
        stroke: "#0f766e",
        strokeDasharray: "6 4",
        strokeWidth: 1.5,
      },
      labelStyle: {
        fill: "#0f766e",
        fontSize: 12,
      },
    });
  }

  return { nodes, edges };
}

function buildFlowEdges({ connections, assets, allAssets, diagramType }) {
  const assetIds = new Set(assets.map((asset) => asId(asset?._id)));
  const assetMap = new Map(allAssets.map((asset) => [asId(asset?._id), asset]));

  return connections
    .filter((connection) => {
      const sourceAssetId = asId(connection?.sourceAssetId);
      const targetAssetId = asId(connection?.targetAssetId);
      if (diagramType === "floor") {
        return assetIds.has(sourceAssetId) && assetIds.has(targetAssetId);
      }
      return assetMap.has(sourceAssetId) && assetIds.has(sourceAssetId) && assetMap.has(targetAssetId) && assetIds.has(targetAssetId);
    })
    .map((connection) => {
      const sourceAsset = assetMap.get(asId(connection?.sourceAssetId));
      const targetAsset = assetMap.get(asId(connection?.targetAssetId));
      const crossLevel =
        asId(sourceAsset?.buildingLevelId) &&
        asId(targetAsset?.buildingLevelId) &&
        asId(sourceAsset?.buildingLevelId) !== asId(targetAsset?.buildingLevelId);

      return {
        id: asId(connection?._id),
        source: asId(connection?.sourceAssetId),
        target: asId(connection?.targetAssetId),
        label:
          connection?.label ||
          findOptionLabel(TECHNICAL_CONNECTION_TYPE_OPTIONS, connection?.connectionType),
        animated: connection?.operationalState === "active",
        style: {
          stroke: crossLevel ? "#0f766e" : "#475569",
          strokeWidth: crossLevel ? 2.2 : 1.5,
        },
        labelStyle: {
          fill: crossLevel ? "#0f766e" : "#334155",
          fontSize: 12,
        },
      };
    });
}

function renderAssetNode(asset) {
  const boardLabel = isDistributionBoardAssetType(asset?.assetType)
    ? renderBoardCapacityHint(asset)
    : null;
  return (
    <div className="min-w-[190px] rounded-sm border border-slate-300 bg-white px-3 py-2 shadow-sm">
      <p className="text-sm font-semibold text-slate-900">
        {asset?.name || "Naamloos asset"}
      </p>
      <p className="text-xs text-slate-500">{asset?.assetTag || "Geen tag"}</p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-400">
        {findOptionLabel(TECHNICAL_ASSET_TYPE_OPTIONS, asset?.assetType)}
      </p>
      {boardLabel ? <p className="mt-2 text-xs text-slate-600">{boardLabel}</p> : null}
    </div>
  );
}

function renderBoardCapacityHint(asset) {
  const totalModules = Number(asset?.technicalSpecs?.totalModules || 0);
  if (!Number.isFinite(totalModules) || totalModules <= 0) return "Verdeelkast";
  return `${totalModules} modules geregistreerd`;
}

function geometryToSvgPaths(geometry, options = {}) {
  const offsetX = Number(options.offsetX || 0);
  const offsetY = Number(options.offsetY || 0);
  if (!geometry || typeof geometry !== "object") return [];

  if (geometry.type === "Polygon") {
    return polygonToPathList(geometry.coordinates, offsetX, offsetY);
  }

  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.flatMap((polygon) => polygonToPathList(polygon, offsetX, offsetY));
  }

  return [];
}

function polygonToPathList(polygon, offsetX, offsetY) {
  return (polygon || []).map((ring) => {
    const points = Array.isArray(ring) ? ring : [];
    return points
      .map((point, index) => {
        const x = Number(point?.[0] || 0) - offsetX;
        const y = Number(point?.[1] || 0) - offsetY;
        return `${index === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .concat("Z")
      .join(" ");
  });
}

function sanitizeTechnicalSpecs(specs = {}) {
  const next = {};
  if (specs.phases) next.phases = String(specs.phases).trim();
  if (specs.ipRating) next.ipRating = String(specs.ipRating).trim();
  for (const field of ["nominalVoltage", "mainFuseAmps", "ratedCurrentAmps", "totalModules"]) {
    const raw = specs[field];
    if (raw === "" || raw === null || raw === undefined) continue;
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) next[field] = parsed;
  }
  return next;
}

function resolveAssetInstallationType(technicalInstallationType, fallbackAssetInstallationType) {
  const mapping = {
    electrical: "electra",
    fire_alarm: "bmi",
    evacuation_alarm: "oai",
    emergency_lighting: "other",
    sprinkler: "other",
    gas_suppression: "other",
    security: "security",
    data: "data",
    mechanical: "mechanical",
    other: "other",
  };
  return (
    mapping[String(technicalInstallationType || "").trim()] ||
    String(fallbackAssetInstallationType || "other").trim() ||
    "other"
  );
}

function filterAssetsForDiagram(
  assets,
  {
    diagramType,
    selectedLocationId,
    selectedBuildingId,
    selectedLevelId,
    selectedTechnicalInstallationId,
    selectedLocationNodeId,
  }
) {
  return (Array.isArray(assets) ? assets : []).filter((asset) => {
    if (selectedLocationId && asId(asset?.locationId) !== selectedLocationId) return false;
    if (selectedTechnicalInstallationId && asId(asset?.installationId) !== selectedTechnicalInstallationId) {
      return false;
    }
    if (selectedLocationNodeId && asId(asset?.locationNodeId) !== selectedLocationNodeId) return false;

    if (diagramType === "site") {
      return true;
    }
    if (diagramType === "vertical") {
      return !selectedBuildingId || asId(asset?.buildingId) === selectedBuildingId;
    }
    if (diagramType === "floor") {
      if (selectedBuildingId && asId(asset?.buildingId) !== selectedBuildingId) return false;
      if (selectedLevelId && asId(asset?.buildingLevelId) !== selectedLevelId) return false;
      return true;
    }
    return true;
  });
}

function buildDiagramName({
  selectedLocation,
  selectedBuilding,
  selectedLevel,
  selectedDiagramType,
}) {
  return [
    selectedLocation?.naam || selectedLocation?.title || "Locatie",
    selectedBuilding?.name || "Gebouw",
    selectedDiagramType === "floor"
      ? selectedLevel?.name || "Verdieping"
      : findOptionLabel(TECHNICAL_DIAGRAM_TYPE_OPTIONS, selectedDiagramType),
  ]
    .filter(Boolean)
    .join(" / ");
}

function getGridPosition(index) {
  const column = index % 4;
  const row = Math.floor(index / 4);
  return {
    x: 60 + column * 250,
    y: 80 + row * 130,
  };
}

function formatAssetLabel(asset) {
  return [asset?.assetTag || "", asset?.name || "Naamloos asset"].filter(Boolean).join(" · ");
}

function formatLocationNodeLabel(node) {
  return [node?.code || "", node?.name || "Naamloze node"].filter(Boolean).join(" · ");
}

function findBuildingName(buildings, buildingId) {
  return (Array.isArray(buildings) ? buildings : []).find(
    (building) => asId(building?._id) === asId(buildingId)
  )?.name;
}

function findLevelName(levels, levelId) {
  return (Array.isArray(levels) ? levels : []).find(
    (level) => asId(level?._id) === asId(levelId)
  )?.name;
}

function findOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || String(value || "-");
}

function asId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value?._id || value?.id || "").trim();
  return String(value).trim();
}

function roundCoordinate(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function buildPlatformTenantHeaders(platformTenantId) {
  const safeTenantId = String(platformTenantId || "").trim();
  if (!safeTenantId) return {};
  return { "x-platform-tenant-id": safeTenantId };
}

function readStoredPlatformTenantId() {
  try {
    return String(window.localStorage.getItem(PLATFORM_TENANT_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

function writeStoredPlatformTenantId(value) {
  try {
    if (value) {
      window.localStorage.setItem(PLATFORM_TENANT_STORAGE_KEY, String(value));
      return;
    }
    window.localStorage.removeItem(PLATFORM_TENANT_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

function resolvePreferredPlatformTenantId(tenants, preferredTenantId) {
  const safePreferredTenantId = String(preferredTenantId || "").trim();
  const activeTenants = Array.isArray(tenants) ? tenants : [];

  if (
    safePreferredTenantId &&
    activeTenants.some((tenant) => asId(tenant?.tenantId) === safePreferredTenantId)
  ) {
    return safePreferredTenantId;
  }

  return asId(activeTenants[0]?.tenantId);
}

function clearTenantScopedState(setters) {
  setters.setClients([]);
  setters.setProjects([]);
  setters.setLocationNodes([]);
  setters.setAssets([]);
  setters.setConnections([]);
  setters.setDiagrams([]);
  setters.setBuildings([]);
  setters.setBuildingLevels([]);
  setters.setTechnicalInstallations([]);
  setters.setBoardCircuits([]);
  setters.setSelectedClientId("");
  setters.setSelectedLocationId("");
  setters.setSelectedBuildingId("");
  setters.setSelectedLevelId("");
  setters.setSelectedLocationNodeId("");
  setters.setSelectedTechnicalInstallationId("");
  setters.setSelectedAssetId("");
  setters.setSelectedDiagramId("");
  setters.setSelectedAssetConnections([]);
  setters.setTagPreview("");
  setters.setNodes([]);
  setters.setEdges([]);
}

function isPlatformRootUser(user) {
  const tenantId = String(user?.tenantId || "").trim();
  if (tenantId !== ROOT_TENANT_ID) return false;
  return getUserRoleNames(user).includes("platformadmin");
}

function getUserRoleNames(user) {
  const roleSet = new Set();

  for (const value of [user?.rol, user?.role]) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized) roleSet.add(normalized);
  }

  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      const normalized = role.trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
      continue;
    }
    if (typeof role === "object") {
      const normalized = String(role?.name || role?.naam || "").trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    }
  }

  return Array.from(roleSet);
}

function userHasAnyPermission(user, permissionKeys = []) {
  const roleNames = getUserRoleNames(user);
  if (roleNames.includes("platformadmin") || roleNames.includes("tenantadmin")) {
    return true;
  }

  const permissionSet = new Set(
    (Array.isArray(user?.permissions) ? user.permissions : [])
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean)
  );

  return (Array.isArray(permissionKeys) ? permissionKeys : []).some((key) =>
    permissionSet.has(String(key || "").trim().toLowerCase())
  );
}
